// Tenant Inventory — supplier catalogue & price lists (EST-CFG-TNT-05-005).
// Backend: api-service app/api/v1/endpoints/inventory.py, app/domain/inventory/
//
// A grid row (`InventoryLine`) is a supplier_product_master joined to its current
// supplier_price_list row; PATCH sends whichever fields changed and the service splits them
// back to the right collection. `effective_from` is the one field that behaves differently:
// changing it creates a NEW price row rather than editing the current one, because a new
// effective date is a new price (see SupplierPriceList in app/domain/inventory/models.py).
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}
interface ApiListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

export interface InventoryLine {
  supplier_product_code: string;
  supplier_code: string;
  supplier_name?: string | null;
  supplier_sku?: string | null;
  manufacturer_sku?: string | null;
  description: string;
  category?: string | null;
  brand?: string | null;
  purchase_uom?: string | null;

  supplier_price_code?: string | null;
  price?: number | null;
  currency: string;
  price_uom?: string | null;
  alt_price?: number | null;
  alt_price_uom?: string | null;
  derived_conversion_factor?: number | null;
  price_note?: string | null;
  branch?: string | null;
  customer?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  // Derived server-side from effective_to vs today, never stored. True means this price list has
  // lapsed: the row is still returned (it is the tenant's last known price) but estimates will
  // NOT bill off it — they fall back to the platform benchmark — so the grid should say so.
  is_expired?: boolean;
  price_source?: 'IMPORT' | 'MANUAL' | null;
  price_row_count: number;

  product_variant_code?: string | null;
  material_code?: string | null;
  item_code?: string | null;
  preferred: boolean;

  minimum_order_quantity?: number | null;
  order_multiple?: number | null;
  supplier_pack_quantity?: number | null;
  lead_time_days?: number | null;
  available: boolean;

  notes?: string | null;
  status: 'ACTIVE' | 'ARCHIVED';
  source: 'IMPORT' | 'MANUAL';
  import_code?: string | null;
  source_page?: number | null;
  updated_on?: string | null;
}

export interface SupplierPriceRow {
  supplier_price_code: string;
  price?: number | null;
  currency: string;
  price_uom?: string | null;
  alt_price?: number | null;
  alt_price_uom?: string | null;
  derived_conversion_factor?: number | null;
  price_note?: string | null;
  branch?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  price_source?: 'IMPORT' | 'MANUAL' | null;
  source_page?: number | null;
  last_updated_at?: string | null;
}

export interface PriceListImport {
  import_code: string;
  file_name: string;
  file_url?: string | null;
  supplier_code?: string | null;
  supplier_name?: string | null;
  branch?: string | null;
  customer?: string | null;
  sales_rep?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  extraction_method?: 'SPREADSHEET' | 'PDF_LLM' | null;
  status: 'EXTRACTING' | 'READY' | 'FAILED';
  page_count: number;
  item_count: number;
  products_created: number;
  products_updated: number;
  prices_created: number;
  prices_updated: number;
  skipped: string[];
  error?: string | null;
  created_on?: string | null;
}

export interface InventorySupplier {
  supplier_code: string;
  supplier_name: string;
  branches?: string[];
}

export interface InventoryFacets {
  suppliers: InventorySupplier[];
  categories: string[];
  brands: string[];
}

export interface InventoryQuery {
  search?: string;
  supplier_code?: string;
  category?: string;
  brand?: string;
  status?: string;
  mapped?: boolean;
  preferred?: boolean;
  import_code?: string;
  // Jump-filter from Settings -> Material & Service Management's Materials tab ("View pricing in
  // Inventory") — that screen carries no negotiated price of its own.
  material_code?: string;
  skip?: number;
  limit?: number;
  sort_by?: string;
  sort_dir?: number;
}

export interface InventoryLinePage {
  lines: InventoryLine[];
  total: number;
}

export type InventoryLineUpdate = Partial<{
  description: string;
  category: string;
  brand: string;
  supplier_sku: string;
  manufacturer_sku: string;
  purchase_uom: string;
  supplier_pack_quantity: number;
  minimum_order_quantity: number;
  order_multiple: number;
  lead_time_days: number;
  available: boolean;
  product_variant_code: string;
  material_code: string;
  item_code: string;
  preferred: boolean;
  notes: string;
  status: 'ACTIVE' | 'ARCHIVED';
  price: number;
  currency: string;
  price_uom: string;
  alt_price: number;
  alt_price_uom: string;
  price_note: string;
  branch: string;
  customer: string;
  effective_from: string;
  effective_to: string;
}>;

export interface InventoryLineCreate extends InventoryLineUpdate {
  supplier_name: string;
  description: string;
}

// One row per variant of a material, carrying whichever price actually applies — this tenant's
// own supplier offer if it has one, else the platform default. Backs the "View pricing" popup on
// Settings -> Material & Service Management's Materials tab.
export interface EffectiveVariantPrice {
  product_variant_code: string;
  variant_name: string;
  purchase_uom: string | null;
  unit_cost: number | null;
  currency: string;
  supplier_code: string | null;
  // Set only when price_source is TENANT_SUPPLIER — the row to PATCH. A PLATFORM_SUPPLIER row has
  // none: editing it forks a new tenant row via createInventoryLine instead.
  supplier_product_code: string | null;
  price_source: 'TENANT_SUPPLIER' | 'PLATFORM_SUPPLIER' | 'NONE';
}

export const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['InventoryLines', 'InventoryLine', 'InventoryImports', 'InventoryFacets'],
  endpoints: (builder) => ({
    searchInventory: builder.query<InventoryLinePage, InventoryQuery>({
      query: (params) => ({ url: '/api/v1/inventory/lines', params: prune(params) }),
      transformResponse: (res: ApiListResponse<InventoryLine>) => ({
        lines: res.data,
        total: res.total,
      }),
      providesTags: ['InventoryLines'],
    }),
    getInventoryFacets: builder.query<InventoryFacets, void>({
      query: () => '/api/v1/inventory/facets',
      transformResponse: (res: ApiResponse<InventoryFacets>) => res.data,
      providesTags: ['InventoryFacets'],
    }),
    listPriceListImports: builder.query<PriceListImport[], void>({
      query: () => '/api/v1/inventory/imports',
      transformResponse: (res: ApiListResponse<PriceListImport>) => res.data,
      providesTags: ['InventoryImports'],
    }),
    getPriceHistory: builder.query<SupplierPriceRow[], string>({
      query: (code) => `/api/v1/inventory/lines/${code}/price-history`,
      transformResponse: (res: ApiListResponse<SupplierPriceRow>) => res.data,
    }),
    importPriceList: builder.mutation<PriceListImport, { file: File; supplierName?: string }>({
      query: ({ file, supplierName }) => {
        const body = new FormData();
        body.append('file', file);
        if (supplierName) body.append('supplier_name', supplierName);
        // No Content-Type header: the browser must set the multipart boundary itself.
        return { url: '/api/v1/inventory/imports', method: 'POST', body };
      },
      transformResponse: (res: ApiResponse<PriceListImport>) => res.data,
      invalidatesTags: ['InventoryLines', 'InventoryImports', 'InventoryFacets'],
    }),
    createInventoryLine: builder.mutation<InventoryLine, InventoryLineCreate>({
      query: (body) => ({ url: '/api/v1/inventory/lines', method: 'POST', body }),
      transformResponse: (res: ApiResponse<InventoryLine>) => res.data,
      invalidatesTags: ['InventoryLines', 'InventoryFacets'],
    }),
    updateInventoryLine: builder.mutation<
      InventoryLine,
      { code: string; changes: InventoryLineUpdate }
    >({
      query: ({ code, changes }) => ({
        url: `/api/v1/inventory/lines/${code}`,
        method: 'PATCH',
        body: changes,
      }),
      transformResponse: (res: ApiResponse<InventoryLine>) => res.data,
      invalidatesTags: ['InventoryLines', 'InventoryFacets'],
    }),
    deleteInventoryLine: builder.mutation<{ supplier_product_code: string }, string>({
      query: (code) => ({ url: `/api/v1/inventory/lines/${code}`, method: 'DELETE' }),
      transformResponse: (res: ApiResponse<{ supplier_product_code: string }>) => res.data,
      invalidatesTags: ['InventoryLines', 'InventoryFacets'],
    }),
    getEffectiveVariantPrices: builder.query<EffectiveVariantPrice[], string>({
      query: (materialCode) => `/api/v1/inventory/materials/${materialCode}/effective-prices`,
      transformResponse: (res: ApiListResponse<EffectiveVariantPrice>) => res.data,
      providesTags: ['InventoryLines'],
    }),
  }),
});

/** Drop empty filters so they don't reach the API as `?category=` and match nothing. */
function prune(params: InventoryQuery): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
  ) as Record<string, string | number | boolean>;
}

export const {
  useSearchInventoryQuery,
  useGetInventoryFacetsQuery,
  useListPriceListImportsQuery,
  useGetPriceHistoryQuery,
  useImportPriceListMutation,
  useCreateInventoryLineMutation,
  useUpdateInventoryLineMutation,
  useDeleteInventoryLineMutation,
  useGetEffectiveVariantPricesQuery,
} = inventoryApi;
